/* generated using openapi-typescript-codegen -- do no edit */
/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { AssignTrainingRequest } from '../models/AssignTrainingRequest';
import type { BackgroundCheckLevelResource } from '../models/BackgroundCheckLevelResource';
import type { BackgroundCheckResource } from '../models/BackgroundCheckResource';
import type { OrderBackgroundCheckRequest } from '../models/OrderBackgroundCheckRequest';
import type { SurveyResource } from '../models/SurveyResource';
import type { TrainingResource } from '../models/TrainingResource';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class MinistrySafeService {

    constructor(public readonly httpRequest: BaseHttpRequest) {}

    /**
     * BGC levels available to the organization
     * @returns any OK
     * @throws ApiError
     */
    public getBackgroundCheckLevels(): CancelablePromise<{
        data: Array<BackgroundCheckLevelResource>;
        meta: {
            /**
             * Caveats that apply to every level
             */
            additional_notes?: Array<string>;
            /**
             * Whether the organization supports user self-pay. Requires quickapp.
             */
            upfront_payment_enabled?: boolean;
        };
    }> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/ministry-safe/background-checks/levels',
            errors: {
                401: `User is unauthorized.`,
                403: `Access Denied`,
            },
        });
    }

    /**
     * Get offer details for MinistrySafe.
     * @returns any OK
     * @throws ApiError
     */
    public getMinistrySafeOffer(): CancelablePromise<{
        data: {
            /**
             * The monthly fee of the integration.
             */
            monthlyFee?: number;
            /**
             * The setup fee of the integration.
             */
            setupFee?: number | null;
            /**
             * Whether or not the setup fee is waived.
             */
            setupFeeWaived?: boolean;
            /**
             * The currency code of the integration.
             */
            currencyCode?: string;
        };
    }> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/ministry-safe/offer',
            errors: {
                401: `User is unauthorized.`,
                403: `Access Denied`,
                404: `Not Found`,
            },
        });
    }

    /**
     * Apply the MinistrySafe offer to the current subscription.
     * @returns void
     * @throws ApiError
     */
    public addMinistrySafeOffer(): CancelablePromise<void> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/ministry-safe/offer',
            errors: {
                400: `Bad request`,
                401: `User is unauthorized.`,
                403: `Access Denied`,
                404: `Not Found`,
            },
        });
    }

    /**
     * Simplified endpoint for setting up MinistrySafe in one call.
     * One-step setup endpoint for MinistrySafe integration (temporary solution for Pendo)
     * @returns any MinistrySafe successfully set up
     * @throws ApiError
     */
    public setupMinistrySafe(): CancelablePromise<{
        /**
         * Status of the operation
         */
        status?: string;
        /**
         * A human-readable message about the operation
         */
        message?: string;
        /**
         * Whether MinistrySafe was already enabled
         */
        already_enabled?: boolean;
    }> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/ministry-safe/offer/setup',
            errors: {
                400: `Bad request`,
                401: `User is unauthorized.`,
                403: `Access Denied`,
                500: `Error setting up MinistrySafe`,
            },
        });
    }

    /**
     * Get the current status of MinistrySafe for the organization.
     * Whether MinistrySafe is enabled for the organization
     * @returns any OK
     * @throws ApiError
     */
    public getMinistrySafeStatus(): CancelablePromise<{
        /**
         * Status of the operation
         */
        status?: string;
        /**
         * Whether the organization has MinistrySafe on its subscription
         */
        is_enabled?: boolean;
    }> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/ministry-safe/status',
            errors: {
                401: `User is unauthorized.`,
                403: `Access Denied`,
            },
        });
    }

    /**
     * Trainings the organization can assign
     * @returns any OK
     * @throws ApiError
     */
    public getSurveys(): CancelablePromise<{
        data: Array<SurveyResource>;
    }> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/ministry-safe/trainings/surveys',
            errors: {
                401: `User is unauthorized.`,
                403: `Access Denied`,
            },
        });
    }

    /**
     * MinistrySafe BGCs for this person
     * @returns any OK
     * @throws ApiError
     */
    public getPersonBackgroundChecks({
        person,
    }: {
        /**
         * Breeze person id
         */
        person: number,
    }): CancelablePromise<{
        data: Array<BackgroundCheckResource>;
    }> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/ministry-safe/users/{person}/background-checks',
            path: {
                'person': person,
            },
            errors: {
                401: `User is unauthorized.`,
                403: `Access Denied`,
            },
        });
    }

    /**
     * Order a BGC for this person and bill the organization
     * @returns any OK
     * @throws ApiError
     */
    public orderBackgroundCheck({
        person,
        requestBody,
    }: {
        /**
         * Breeze person id
         */
        person: number,
        requestBody: OrderBackgroundCheckRequest,
    }): CancelablePromise<{
        data: BackgroundCheckResource;
    }> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/ministry-safe/users/{person}/background-checks',
            path: {
                'person': person,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                400: `Bad request`,
                401: `User is unauthorized.`,
                403: `Access Denied`,
            },
        });
    }

    /**
     * MinistrySafe trainings for this person, newest first
     * @returns any OK
     * @throws ApiError
     */
    public getPersonTrainings({
        person,
    }: {
        /**
         * Breeze person id
         */
        person: number,
    }): CancelablePromise<{
        data: Array<TrainingResource>;
    }> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/ministry-safe/users/{person}/trainings',
            path: {
                'person': person,
            },
            errors: {
                401: `User is unauthorized.`,
                403: `Access Denied`,
            },
        });
    }

    /**
     * Assign a training to this person with MinistrySafe emailing them
     * @returns any OK
     * @throws ApiError
     */
    public assignTraining({
        person,
        requestBody,
    }: {
        /**
         * Breeze person id
         */
        person: number,
        requestBody: AssignTrainingRequest,
    }): CancelablePromise<{
        data: {
            /**
             * Link that takes the person straight into the training, for use if they miss the email
             */
            direct_login_url?: string;
        };
    }> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/ministry-safe/users/{person}/trainings',
            path: {
                'person': person,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                400: `Bad request`,
                401: `User is unauthorized.`,
                403: `Access Denied`,
            },
        });
    }

}
